import React, { useState, useEffect } from "react";
import { CalendarClock, Save, Loader2, CheckCircle2, AlertCircle, Pencil, X } from "lucide-react";
import { adminService } from "../services/adminService";

const formatUpdatedAt = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const AdminAnalysisCooldownPage = () => {
  const [cooldownDays, setCooldownDays] = useState(2);
  const [savedDays, setSavedDays] = useState(2);
  const [savedHours, setSavedHours] = useState(48);
  const [lastUpdated, setLastUpdated] = useState("");
  const [updatedBy, setUpdatedBy] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }

  useEffect(() => {
    let cancelled = false;
    const fetchSetting = async () => {
      setLoading(true);
      try {
        const res = await adminService.getAnalysisCacheDays();
        if (!cancelled) {
          const days = Number(res?.data?.days ?? 2);
          const hours = Number(res?.data?.hours ?? days * 24);
          const clamped = Math.min(30, Math.max(1, days || 2));
          setCooldownDays(clamped);
          setSavedDays(clamped);
          setSavedHours(hours || clamped * 24);
          setLastUpdated(res?.data?.lastUpdated ?? "");
          setUpdatedBy(res?.data?.updatedBy ?? "");
        }
      } catch {
        if (!cancelled) {
          setCooldownDays(2);
          setSavedDays(2);
          setSavedHours(48);
          setLastUpdated("");
          setUpdatedBy("");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSetting();
    return () => { cancelled = true; };
  }, []);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminService.updateAnalysisCacheDays(cooldownDays);
      const nextDays = Number(res?.data?.days ?? cooldownDays);
      const nextHours = Number(res?.data?.hours ?? nextDays * 24);
      setSavedDays(nextDays);
      setCooldownDays(nextDays);
      setSavedHours(nextHours);
      setLastUpdated(res?.data?.lastUpdated ?? "");
      setUpdatedBy(res?.data?.updatedBy ?? "");
      setEditing(false);
      showToast("success", `Cache days updated to ${nextDays} day${nextDays === 1 ? "" : "s"}.`);
    } catch (err) {
      showToast("error", err.message || "Failed to save setting.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setCooldownDays(savedDays);
    setEditing(false);
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
            toast.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {toast.type === "success" ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <CalendarClock className="w-5 h-5 text-[#5B4FCF]" />
          <h1 className="text-xl font-bold text-brand-text">Cache Days Setup</h1>
        </div>
        <p className="text-sm text-brand-muted">
          Configure how many days analysis results stay cached before a fresh run is required.
        </p>
      </div>

      {/* Card */}
      <div className="bg-white border border-surface-border rounded-xl p-6 shadow-sm">
        <h2 className="text-base font-semibold text-brand-text mb-1">Cache Retention</h2>
        <p className="text-sm text-brand-muted mb-5">
          Admins can control the cache window in days through the new cache settings endpoint.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-brand-muted text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading current setting…
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
              Cache duration
            </label>

            {editing ? (
              /* ── Edit mode ── */
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <input
                    type="number"
                    min="1"
                    max="30"
                    step="1"
                    value={cooldownDays}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      if (nextValue === "") {
                        setCooldownDays("");
                        return;
                      }
                      const parsed = Number(nextValue);
                      if (Number.isNaN(parsed)) return;
                      setCooldownDays(Math.min(30, Math.max(1, parsed)));
                    }}
                    className="w-full sm:w-48 px-4 py-2.5 border border-surface-border rounded-lg bg-white text-sm font-medium text-brand-text focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/30"
                  />
                  <span className="text-sm text-brand-muted whitespace-nowrap">Days</span>
                </div>
                {/* Quick presets */}
                <div className="flex items-center gap-2">
                  {[2, 7].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setCooldownDays(d)}
                      className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                        Number(cooldownDays) === d
                          ? "border-[#5B4FCF] bg-[#5B4FCF]/10 text-[#5B4FCF]"
                          : "border-surface-border text-brand-muted hover:bg-surface/60"
                      }`}
                    >
                      {d} days
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !cooldownDays}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-[#5B4FCF] text-white hover:bg-[#4a40b8] shadow-sm transition-all disabled:opacity-60"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border border-surface-border text-brand-muted hover:text-brand-text hover:bg-surface transition-all"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            ) : (
              /* ── View mode ── */
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2.5 border border-surface-border rounded-lg bg-surface text-sm font-semibold text-brand-text w-48">
                  <CalendarClock className="w-4 h-4 text-[#5B4FCF] flex-shrink-0" />
                  {savedDays} Days
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border border-[#5B4FCF]/40 text-[#5B4FCF] hover:bg-[#5B4FCF]/5 transition-all"
                >
                  <Pencil className="w-4 h-4" />
                  Edit
                </button>
              </div>
            )}

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-surface-border bg-surface/40 px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-brand-light">Current Days</p>
                <p className="mt-1 text-lg font-semibold text-brand-text">{savedDays}</p>
              </div>
              <div className="rounded-lg border border-surface-border bg-surface/40 px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-brand-light">Current Hours</p>
                <p className="mt-1 text-lg font-semibold text-brand-text">{savedHours}</p>
              </div>
              <div className="rounded-lg border border-surface-border bg-surface/40 px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-brand-light">Updated By</p>
                <p className="mt-1 text-sm font-semibold text-brand-text break-all">{updatedBy || "—"}</p>
              </div>
            </div>

            <div className="mt-3 rounded-lg border border-surface-border bg-white px-4 py-3">
              <p className="text-[11px] font-medium uppercase tracking-wide text-brand-light">Last Updated</p>
              <p className="mt-1 text-sm text-brand-text">{formatUpdatedAt(lastUpdated)}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalysisCooldownPage;
